import React, { useCallback, useMemo, useState } from "react";
import Styles from "./LegerViewType.module.css";
import { useDispatch, useSelector } from "react-redux";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { Accordion, AccordionDetails } from "../../../commons/Accordion";
import { formatDate, formatSlotTime } from "../../../helpers/dateFormat";
import {
  STATUSES,
  useConnectionData,
  useModals,
} from "../../ConsumerGenerator/ListingContainer/AvailableListing/AvailableListing";
import { fetchSchedule } from "../../../Redux/Slices/scheduleSlice";
import { USER_TYPES } from "../LedgerPageDashboardActive/LedgerPageDashboardActive";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import { withdrawTerms } from "../../../Services/BuySell/Listing";
import {
  fetchActiveConnection,
  fetchMainScreenActiveConnection,
  fetchSelfCapacity,
} from "../../../Redux/Slices/CapacitySlice";
import ViewFinalizeTerms from "../../Modals/ConGen/ViewFinalizeTerms/ViewFinalizeTerms";
import AcceptTermsCondition from "../../Modals/ConGen/AcceptTermsCondition/AcceptTermsCondition";
import Charges from "../../Modals/ConGen/Charges/Charges";
import ViewConsent from "../../Modals/ConGen/ViewConsent/ViewConsent";
import UploadDocModal from "../../Modals/ConGen/UploadFiles/UploadFiles";
import ScheduleModal from "../../Modals/Schedule/ScheduleModal";
import UploadLDC from "../../Modals/ConGen/UploadLCD/UploadLDC";
import UploadConsent from "../../Modals/ConGen/UploadConsent/UploadConsent";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { openBilateralChat } from "../../Modals/Banking/CreateEdit/helpers";
import ViewDoc from "../../Modals/ConGen/ViewDoc/ViewDoc";

// Left Side Buttons Component
export const LeftSideBtns = React.memo(
  ({ clickedIndex, capacityId, showEditDocs = true }) => {
    const dispatch = useDispatch();
    const { user, finFinal, mainScreenMatchData } = useConnectionData();
    const { modals, toggleModal } = useModals();
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const listingId = useSelector(
      (state) => state?.CapacitySlice?.activeListingId
    );

    const connection = useMemo(() => {
      return mainScreenMatchData?.[clickedIndex];
    }, [mainScreenMatchData, clickedIndex]);

    const handleViewSchedule = useCallback(async () => {
      if (!connection?.id) return;

      dispatch(fetchSchedule(connection.id));
      setTimeout(() => {
        toggleModal("schedule", true);
      }, 1000);
    }, [connection?.id, dispatch, toggleModal]);

    const buttonConfig = useMemo(() => {
      const shouldShowViewTerms = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conTermsAcceptanceStatus) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genTermsAcceptanceStatus)
        );
      };

      const shouldShowViewWithdrawTerms = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.conTermsAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.genTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING
        );
      };

      const shouldShowViewCharges = () => {
        return finFinal?.status !== STATUSES.TERMS_INITIATED;
      };

      const shouldShowViewDocs = () => {
        return (
          connection?.genAgreementAcceptanceStatus &&
          connection?.conAgreementAcceptanceStatus
        );
      };

      const shouldShowEditDocs = () => {
        return (
          (user === USER_TYPES.GENERATOR &&
            connection?.genAgreementAcceptanceStatus &&
            !connection?.conAgreementAcceptanceStatus) ||
          (user === USER_TYPES.CONSUMER &&
            connection?.conAgreementAcceptanceStatus &&
            !connection?.genAgreementAcceptanceStatus)
        );
      };

      const shouldShowViewConsent = () => {
        return (
          (user === USER_TYPES.CONSUMER && connection?.conConsentDocId) ||
          (user === USER_TYPES.GENERATOR && connection?.genConsentDocId)
        );
      };

      const shouldShowViewSchedule = () => {
        return connection?.status === STATUSES.SCHEDULED;
      };

      return [
        {
          show: shouldShowViewTerms(),
          onClick: () => toggleModal("terms", true),
          text: "View Terms",
        },
        {
          show: showEditDocs && shouldShowEditDocs(),
          onClick: () => toggleModal("EditDocs", true),
          text: "Edit Docs",
        },
        {
          show: shouldShowViewCharges(),
          onClick: () => toggleModal("charges", true),
          text: "View Charges",
        },
        {
          show: shouldShowViewDocs(),
          onClick: () => toggleModal("docs", true),
          text: "View Docs",
        },
        {
          show: shouldShowViewConsent(),
          onClick: () => toggleModal("consent", true),
          text: "View Consent",
        },
        {
          show: shouldShowViewSchedule(),
          onClick: handleViewSchedule,
          text: "View Schedule",
        },
      ];
    }, [
      showEditDocs,
      handleViewSchedule,
      user,
      connection?.conTermsAcceptanceStatus,
      connection?.genTermsAcceptanceStatus,
      connection?.status,
      connection?.genAgreementAcceptanceStatus,
      connection?.conAgreementAcceptanceStatus,
      connection?.conConsentDocId,
      connection?.genConsentDocId,
      finFinal?.status,
      toggleModal,
    ]);

    if (!connection) {
      return null;
    }

    function getAllContractIds(data) {
      const ids = [];

      // Top-level contractId (if present)
      if (data?.capacityConsumer?.contractId !== undefined) {
        ids.push(data.capacityConsumer.contractId);
      }
      if (data?.capacityGenerator?.contractId !== undefined) {
        ids.push(data.capacityGenerator.contractId);
      }

      // From matches.generators
      if (Array.isArray(data?.matches)) {
        data.matches.forEach((match) => {
          (match.generators || []).forEach((gen) => {
            if (gen.contractId !== undefined) {
              ids.push(gen.contractId);
            }
          });
          (match.consumers || []).forEach((con) => {
            if (con.contractId !== undefined) {
              ids.push(con.contractId);
            }
          });
        });
      }

      // Remove duplicates
      return Array.from(new Set(ids));
    }

    async function handleWithdraw() {
      const finalizedIds = getAllContractIds(connection);
      if (finalizedIds?.length === 0) {
        return ErrorMessage("Please Select Requisition(s)");
      }
      let payload = {
        contractIds: finalizedIds,
      };
      try {
        setWithdrawLoading(true);
        const res = await withdrawTerms(payload);
        if (res?.data?.code === 200) {
          dispatch(
            fetchActiveConnection({
              curr: 0,
              capId: listingId,
              isTermsAccepted: null,
            })
          );
          dispatch(fetchSelfCapacity({ user }));
          toggleModal("withdrawTerms", false);
        }
      } catch (error) {
        console.error("Error withdrawing terms:", error);
      } finally {
        setWithdrawLoading(false);
      }
    }

    return (
      <>
        {/* Modals */}
        {modals.terms && (
          <ViewFinalizeTerms
            open={modals.terms}
            handleClose={() => toggleModal("terms", false)}
            data={connection}
          />
        )}
        {modals.withdrawTerms && (
          <AcceptTermsCondition
            open={modals.withdrawTerms}
            handleClose={() => toggleModal("withdrawTerms", false)}
            data={connection}
            // handleTermsSubmit={handleTermsSubmit}
            wholeData={connection}
            handleWithdraw={handleWithdraw}
            withdrawLoading={withdrawLoading}
          />
        )}

        {modals.charges && (
          <Charges
            open={modals.charges}
            onClose={() => toggleModal("charges", false)}
            contractId={connection?.id}
          />
        )}

        {modals.consent && (
          <ViewConsent
            connectionId={connection.id}
            open={modals.consent}
            onClose={() => toggleModal("consent", false)}
          />
        )}

        {modals.docs && (
          <ViewDoc
            connectionId={connection.id}
            open={modals.docs}
            handleClose={() => toggleModal("docs", false)}
          />
        )}

        {modals.schedule && (
          <ScheduleModal
            connectionId={connection.id}
            open={modals.schedule}
            handleClose={() => toggleModal("schedule", false)}
          />
        )}

        {/* Buttons */}
        <div className={Styles.leftSideBtnContainer}>
          {buttonConfig
            .filter((button) => button.show)
            .map((button, index) => (
              <button
                key={`left-btn-${index}`}
                onClick={button.onClick}
                className={Styles.leftSideBtn}
              >
                {button.text}
                <CallMadeIcon
                  style={{
                    fontSize: "8px",
                    position: "absolute",
                    top: "-0px",
                  }}
                />
              </button>
            ))}
        </div>
      </>
    );
  }
);

// Right Side Buttons Component
export const RightSideBtn = React.memo(
  ({ clickedIndex, capacityId, viewType }) => {
    const navigate = useNavigate();
    const { user, finFinal, mainScreenMatchData } = useConnectionData();
    const { modals, toggleModal } = useModals();

    const connection = useMemo(() => {
      return mainScreenMatchData?.[clickedIndex];
    }, [mainScreenMatchData, clickedIndex]);

    const conditionalHelpers = useMemo(() => {
      const shouldShowUploadDocs = () => {
        return (
          connection?.status === STATUSES.TERMS_ACCEPTED &&
          !connection?.conAgreementAcceptanceStatus &&
          !connection?.genAgreementAcceptanceStatus
        );
      };

      const shouldShowWaitingDocApproval = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conAgreementAcceptanceStatus &&
            !connection?.genAgreementAcceptanceStatus) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genAgreementAcceptanceStatus &&
            !connection?.conAgreementAcceptanceStatus)
        );
      };

      const shouldShowWaitingTermsApproval = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.conTermsAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.genTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING
        );
      };

      const shouldShowTermsApproved = () => {
        return (
          connection?.status === STATUSES.TERMS_INITIATED &&
          user === USER_TYPES.CONSUMER &&
          connection?.genTermsAcceptanceStatus &&
          connection?.conTermsAcceptanceStatus
        );
      };

      const shouldShowWaitingOtherParties = () => {
        return (
          connection?.status === STATUSES.TERMS_INITIATED &&
          user === USER_TYPES.GENERATOR &&
          connection?.genTermsAcceptanceStatus &&
          connection?.conTermsAcceptanceStatus
        );
      };

      const shouldShowWaitingConsentUpload = () => {
        return (
          (user === USER_TYPES.CONSUMER &&
            connection?.conConsentDocId &&
            !connection?.genConsentDocId) ||
          (user === USER_TYPES.GENERATOR &&
            connection?.genConsentDocId &&
            !connection?.conConsentDocId)
        );
      };

      const shouldShowApproveTermsBtn = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.genTermsAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.conTermsAcceptanceStatus)) &&
          connection?.status === STATUSES.PENDING
        );
      };

      const shouldShowApproveDocs = () => {
        return (
          ((user === USER_TYPES.CONSUMER &&
            connection?.genAgreementAcceptanceStatus) ||
            (user === USER_TYPES.GENERATOR &&
              connection?.conAgreementAcceptanceStatus)) &&
          connection?.status !== STATUSES.AGREEMENT_ACCEPTED &&
          connection?.status !== STATUSES.SCHEDULED
        );
      };

      const shouldShowUploadConsent = () => {
        return (
          connection?.status === STATUSES.AGREEMENT_ACCEPTED &&
          !(
            (user === USER_TYPES.CONSUMER && connection?.conConsentDocId) ||
            (user === USER_TYPES.GENERATOR && connection?.genConsentDocId)
          )
        );
      };

      const shouldShowGenerateSchedule = () => {
        return (
          connection?.status === STATUSES.AGREEMENT_ACCEPTED &&
          connection?.conConsentDocId &&
          connection?.genConsentDocId &&
          connection?.status !== STATUSES.SCHEDULED
        );
      };

      return {
        shouldShowUploadDocs,
        shouldShowWaitingDocApproval,
        shouldShowWaitingTermsApproval,
        shouldShowTermsApproved,
        shouldShowWaitingOtherParties,
        shouldShowWaitingConsentUpload,
        shouldShowApproveTermsBtn,
        shouldShowApproveDocs,
        shouldShowUploadConsent,
        shouldShowGenerateSchedule,
      };
    }, [connection, user]);

    const statusMessages = useMemo(
      () => [
        {
          show: conditionalHelpers.shouldShowWaitingDocApproval(),
          text: "Waiting Document Approval",
        },
        {
          show: conditionalHelpers.shouldShowWaitingTermsApproval(),
          text: "Waiting Terms Approval",
        },
        {
          show: conditionalHelpers.shouldShowTermsApproved(),
          text: "Terms Approved",
        },
        {
          show: conditionalHelpers.shouldShowWaitingOtherParties(),
          text: "Waiting for other parties",
        },
        {
          show:
            conditionalHelpers.shouldShowWaitingConsentUpload() &&
            (window.location.pathname.includes("ongoing") ||
              window.location.pathname.includes("tender_active")),
          text: "Waiting Consent Upload",
        },
        {
          show:
            (conditionalHelpers.shouldShowUploadConsent() ||
              conditionalHelpers.shouldShowWaitingConsentUpload() ||
              conditionalHelpers.shouldShowGenerateSchedule() ||
              connection?.status === STATUSES.SCHEDULED) &&
            window.location.pathname.includes("bilateral"),
          text: "Moved to Ongoing Connections",
        },
      ],
      [conditionalHelpers, connection?.status]
    );

    const buttons = useMemo(
      () => [
        {
          show: conditionalHelpers.shouldShowUploadDocs(),
          onClick: () => toggleModal("uploadFiles", true),
          text: "Upload Docs",
        },
        {
          show: conditionalHelpers.shouldShowApproveTermsBtn(),
          onClick: () => navigate(`/bilateral?id=${finFinal?.id}`),
          text: "Approve Terms",
        },
        {
          show: conditionalHelpers.shouldShowApproveDocs(),
          onClick: () => toggleModal("approveDocs", true),
          text: "Approve Docs",
        },
        {
          show:
            conditionalHelpers.shouldShowUploadConsent() &&
            !window.location.pathname.includes("bilateral"),
          onClick: () => toggleModal("uploadConsent", true),
          text: "Upload Consent",
        },
        {
          show:
            conditionalHelpers.shouldShowGenerateSchedule() &&
            !window.location.pathname.includes("bilateral"),
          onClick: () => toggleModal("uploadLDC", true),
          text: "Generate Schedule",
        },
      ],
      [conditionalHelpers, toggleModal, navigate, finFinal?.id]
    );

    if (!connection) {
      return null;
    }

    return (
      <>
        {/* Modals */}
        {modals.uploadFiles && (
          <UploadDocModal
            capacityId={capacityId}
            data={connection}
            connectionId={connection.id}
            open={modals.uploadFiles}
            handleClose={() => toggleModal("uploadFiles", false)}
          />
        )}

        {modals.approveDocs && (
          <UploadDocModal
            viewType={viewType}
            capacityId={capacityId}
            data={connection}
            agreementStatus={
              (user === "consumer" &&
                connection?.genAgreementAcceptanceStatus) ||
              (user === "generator" && connection?.conAgreementAcceptanceStatus)
            }
            connectionId={connection.id}
            open={modals.approveDocs}
            handleClose={() => toggleModal("approveDocs", false)}
          />
        )}

        {modals.uploadLDC && (
          <UploadLDC
            capacityId={capacityId}
            connectionId={connection.id}
            open={modals.uploadLDC}
            onClose={() => toggleModal("uploadLDC", false)}
          />
        )}

        {modals.uploadConsent && (
          <UploadConsent
            capacityId={capacityId}
            connectionId={connection.id}
            open={modals.uploadConsent}
            onClose={() => toggleModal("uploadConsent", false)}
          />
        )}

        {/* Status Messages */}
        {statusMessages
          .filter((message) => message.show)
          .map((message, index) => (
            <span
              key={`status-${index}`}
              className={Styles.waitingApprovalText}
            >
              {message.text}
            </span>
          ))}

        {/* Action Buttons */}

        {buttons
          .filter((button) => button.show)
          .map((button, index) => (
            <button
              key={`right-btn-${index}`}
              onClick={button.onClick}
              className={Styles.rightSideBtn}
            >
              {button.text}
            </button>
          ))}
      </>
    );
  }
);

const LedgerUploadDocs = ({
  data,
  wholeData,
  capacityId,
  viewType,
  setHideBucketFill,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(
    (state) => state.persistedReducer.user?.activeRole
  )?.toLowerCase();
  const [expanded, setExpanded] = useState(0);
  const handleAccordionChange = (val) => {
    if (val === expanded) {
      setExpanded("");
    } else {
      setExpanded(val);
    }
  };
  const locationUrl = useLocation();

  const listingId = capacityId || locationUrl?.search?.split("=")[1];

  useEffect(() => {
    setHideBucketFill(true);
  }, [setHideBucketFill]);

  useEffect(() => {
    dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
  }, [dispatch, listingId]);

  const userDataKey =
    user === "consumer" ? "capacityGenerator" : "capacityConsumer";
  const mainScreenMatchData = useSelector(
    (state) => state?.CapacitySlice?.mainScreenActiveConnection
  );

  const wholeDataLocal = mainScreenMatchData;
  // Add original index to each item
  const wholeDataWithIndex = wholeDataLocal?.map((ele, idx) => ({
    ...ele,
    index: idx,
  }));

  // Filter data based on pathname
  const filteredData = window.location.pathname.includes("ongoing")
    ? wholeDataWithIndex?.filter(
        (ele) =>
          ele.status === "TERMS_ACCEPTED" ||
          ele.status === "AGREEMENT_ACCEPTED" ||
          ele.status === "FINAL" ||
          ele.status === "ONGOING" ||
          ele.status === "SCHEDULED"
      )
    : wholeDataWithIndex;

  const fromUser =
    user === "consumer" ? "capacityConsumer" : "capacityGenerator";
  const toUser = user === "consumer" ? "capacityGenerator" : "capacityConsumer";

  const handleOpenChat = (fromName, toName) => {
    let chatId;

    if (user === "CONSUMER") {
      chatId = `${fromName?.split("_")?.[0]}_${toName?.split("_")?.[0]}`;
    } else {
      chatId = `${toName?.split("_")?.[0]}_${fromName?.split("_")?.[0]}`;
    }
    console.debug("chatId", chatId);
    openBilateralChat(dispatch, {
      header: fromName,
      fromUser: fromName,
      toUser: toName,
      chatId: chatId,
    });
  };

  return (
    <>
      <div className={Styles.accordionSummaryContainer}>
        {filteredData?.map((ele, index) => {
          if (
            (ele.status === "TERMS_ACCEPTED" || ele.status === "ONGOING") &&
            window.location.pathname.includes("ongoing")
          )
            return null;
          return (
            <div
              className={
                expanded === index ? "" : Styles.detailsMainDrop_inactive
              }
            >
              <Accordion
                style={{ backgroundColor: "#f3f5f8" }}
                expanded={expanded === index}
                onChange={() => handleAccordionChange(index)}
              >
                <AccordionDetails
                  style={{ backgroundColor: "white", borderRadius: "8px" }}
                >
                  <>
                    <div className={Styles.accordionDetailsContainerUploadDocs}>
                      <div
                        style={{ paddingRight: "15px" }}
                        className={`display_flex_row ${Styles.accHeaderTable_name}`}
                      >
                        <div
                          style={{ marginLeft: "0.7%" }}
                          className={`table_text_align_left ${Styles.nameCompanyDetails}`}
                        >
                          <div>
                            {ele?.[userDataKey]?.companyDisplayName ||
                              ele?.[userDataKey]?.consumerName ||
                              ele?.[userDataKey]?.generatorName}
                          </div>
                          <div>{ele?.[userDataKey]?.name}</div>
                        </div>
                        <div className={Styles.chatButtonContainer}>
                          <button
                            style={{ marginRight: "calc(100vw - 95vw)" }}
                            type="button"
                            className={Styles.chatButton}
                            onClick={() =>
                              handleOpenChat(
                                ele?.[fromUser]?.name,
                                ele?.[toUser]?.name
                              )
                            }
                          >
                            Chat
                          </button>
                        </div>
                      </div>
                      {ele?.bilateralConnections?.map((elem, i) => {
                        if (elem.status === "REJECTED") return null;
                        return (
                          <table className={Styles.ledgerTableHeading}>
                            <tbody>
                              <tr style={{ height: "40px" }}>
                                <th
                                  style={{
                                    textAlign: "left",
                                    paddingLeft: "30px",
                                    width:
                                      user === "consumer" ? "16.6%" : "20%",
                                    color: "#8C8A96",
                                  }}
                                  className=""
                                >
                                  Connection {i + 1}
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {elem?.quantity} (MW)
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {console.debug("elem", elem)}
                                  {elem?.energyRateType === "FIXED"
                                    ? `${elem?.amount}`
                                    : elem?.energyRateType === "DAM_PLUS"
                                    ? `DAM+${elem?.amount}`
                                    : `DAM-${elem?.amount}`}
                                  (₹/kWh)
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {formatDate(elem?.dateStartAt)} -{" "}
                                  {formatDate(elem?.dateEndAt)}
                                </th>
                                <th
                                  className=""
                                  style={{
                                    width:
                                      user === USER_TYPES.CONSUMER
                                        ? "16.6%"
                                        : "20%",
                                  }}
                                >
                                  {formatSlotTime(elem?.slotStartAt)}-
                                  {formatSlotTime(elem?.slotEndAt)}
                                </th>
                              </tr>
                            </tbody>
                          </table>
                        );
                      })}

                      <div
                        style={{ paddingRight: "calc(100vw - 95vw)" }}
                        className={Styles.statusBtnContainer}
                      >
                        <LeftSideBtns
                          capacityId={capacityId}
                          clickedIndex={index}
                        />

                        <RightSideBtn
                          viewType={viewType}
                          capacityId={capacityId}
                          clickedIndex={index}
                        />
                      </div>
                    </div>
                  </>
                </AccordionDetails>
              </Accordion>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default LedgerUploadDocs;
